import React, { useState, useEffect } from 'react'
import useBreakpoint from '#src/hooks/useBreakpoint'
import classNames from 'classnames'
import { Spring } from 'react-spring/renderprops.cjs'
import { useTrail, animated, config } from 'react-spring'
import { Grid, GridItem } from '@deal/components'
import { SpotlightExpertFragment } from '#src/generated/graphql'
import ArrowLeft from '../../arrow-left.svg'
import ArrowRight from '../../arrow-right.svg'
import DismissIcon from '../../dismiss.svg'
import styles from './styles.css'

interface ExpertPlayerProps {
  onNextExpert: () => void
  onPreviousExpert: () => void
  canGoToPrevExpert: boolean
  canGoToNextExpert: boolean
  featuredExpert?: SpotlightExpertFragment
  onDismissVideo: () => void
}

interface HeaderTextProps {
  featuredExpert: SpotlightExpertFragment
}

const HeaderText: React.SFC<HeaderTextProps> = ({ featuredExpert }) => {
  const expertTextArr = [
    featuredExpert.displayName,
    featuredExpert.expertProfile ? featuredExpert.expertProfile.spotlightHeadline : ''
  ]
  const animationConfig = { tension: 280, friction: 60 }
  const expertTextTrail = useTrail(expertTextArr.length, {
    config: animationConfig,
    opacity: 1,
    x: 0,
    from: {
      opacity: 0,
      x: -20
    }
  })

  return (
    <>
      {expertTextTrail.map(({ x, opacity }, index) => {
        return (
          <animated.div
            key={index}
            className={index === 0 ? styles.expertName : styles.expertBody}
            style={{
              opacity,
              transform: `translate3d(0, ${x}px, 0)`
            }}
          >
            {expertTextArr[index]}
          </animated.div>
        )
      })}
    </>
  )
}

const ExpertPlayer: React.SFC<ExpertPlayerProps> = ({
  featuredExpert,
  onDismissVideo,
  onNextExpert,
  onPreviousExpert,
  canGoToNextExpert,
  canGoToPrevExpert
}) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)
  const [isOverlayContentHidden, setIsOverlayContentHidden] = useState(false)
  const [hasComponentMounted, setHasComponentMounted] = useState(false)

  useEffect(() => {
    setHasComponentMounted(true)
  }, [])

  if (
    !featuredExpert ||
    !featuredExpert.expertProfile ||
    !featuredExpert.expertProfile.introductionVideoLink
  ) {
    throw new Error('Attempted to load featured expert player without introduction video')
  }

  const isLargerThanSm = useBreakpoint('sm')
  const isLargerThanMd = useBreakpoint('md')
  const isLargerThanLg = useBreakpoint('lg')

  // Start with the smallest video source and work towards the largest
  let videoSource = featuredExpert.expertProfile.introductionVideoLink.smallUrl
  if (isLargerThanSm) {
    videoSource = featuredExpert.expertProfile.introductionVideoLink.mediumUrl
  }

  if (isLargerThanMd) {
    videoSource = featuredExpert.expertProfile.introductionVideoLink.largeUrl
  }

  if (isLargerThanLg) {
    videoSource = featuredExpert.expertProfile.introductionVideoLink.xLargeUrl
  }

  return (
    <div className={styles.player}>
      <div className={styles.videoContainer}>
        <div className={styles.mobilePlayerBar}>
          <div>
            <button className={styles.mobileNavigationButton} onClick={() => onDismissVideo()}>
              <DismissIcon width={22} height={22} />
            </button>
          </div>
          <div className={styles.mobileNavigationArrows}>
            {canGoToPrevExpert && (
              <button className={styles.mobileNavigationButton} onClick={() => onPreviousExpert()}>
                <ArrowLeft width={10} height={15} />
              </button>
            )}
            {canGoToNextExpert && (
              <button className={styles.mobileNavigationButton} onClick={() => onNextExpert()}>
                <ArrowRight width={10} height={15} />
              </button>
            )}
          </div>
        </div>
        <video
          playsInline
          controls
          autoPlay
          className={styles.video}
          src={videoSource}
          onEnded={() => {
            if (canGoToNextExpert) {
              onNextExpert()
            } else {
              onDismissVideo()
            }
          }}
        />
      </div>

      <Spring
        delay={4500}
        from={{
          opacity: 1
        }}
        to={{
          opacity: 0
        }}
        onRest={() => {
          setIsAnimationComplete(true)
          setIsOverlayContentHidden(true)
        }}
        config={{ ...config.default, duration: 500 }}
      >
        {props => {
          return (
            <div
              className={classNames(styles.videoOverlay, {
                [styles.hide]: isAnimationComplete
              })}
              style={props}
            >
              <Grid>
                <GridItem col={2} className={styles.gridWrapper}>
                  <div
                    className={classNames(styles.copy, {
                      [styles.hide]: isOverlayContentHidden
                    })}
                  >
                    {hasComponentMounted && <HeaderText featuredExpert={featuredExpert} />}
                  </div>
                </GridItem>
              </Grid>
            </div>
          )
        }}
      </Spring>
    </div>
  )
}

export default ExpertPlayer
